import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getAttendanceDetails,
  getAttendanceList,
  getLeavesHistoryList,
  approveAttendance,
  getLeaveDetails,
  approveRejectLeave,
  getPendingLeavesList,
} from '../services/attendanceService';
import { AttendanceInitialStateType } from '../utils/Types';
import {
  API_STATUS,
  BlankPagination,
  leaveDetailsBlank,
} from '../utils/Constant';
import { isAPISuccess } from '../utils/Util';
import {
  getTargetsDropValues,
  getStatusDrop,
} from '../services/dropDownService';

export const fetchAttendanceList = createAsyncThunk(
  'attendance/fetchAttendanceList',
  async (data: any) => {
    const res = await getAttendanceList(data);
    return res.data;
  }
);
export const fetchAttendanceDetails = createAsyncThunk(
  'attendance/fetchAttendanceDetails',
  async (params: any) => {
    const res = await getAttendanceDetails(params);
    return res.data;
  }
);

export const fetchLeaveDetails = createAsyncThunk(
  'attendance/fetchLeaveDetails',
  async (params: any) => {
    const res = await getLeaveDetails(params);
    return res.data;
  }
);

export const approveOrRejectLeave = createAsyncThunk(
  'attendance/approveOrRejectLeave',
  async (data: any) => {
    const res = await approveRejectLeave(data);
    return res.data;
  }
);

export const fetchLeavesHistoryList = createAsyncThunk(
  'attendance/fetchLeavesHistoryList',
  async (data: any) => {
    const res = await getLeavesHistoryList(data);
    return res.data;
  }
);
export const fetchLeavesPendingList = createAsyncThunk(
  'attendance/fetchLeavesPendingList',
  async (data: any) => {
    const res = await getPendingLeavesList(data);
    return res.data;
  }
);

export const approveOrRejectAttendance = createAsyncThunk(
  'attendance/approveOrRejectAttendance',
  async (data: any) => {
    const res = await approveAttendance(data);
    return res.data;
  }
);
export const fetchStatusDrops = createAsyncThunk(
  'attendance/fetchStatusDrops',
  async (params: any) => {
    const res = await getStatusDrop(params);
    return res.data;
  }
);

export const fetchLeaveTypeDrops = createAsyncThunk(
  'attendance/fetchLeaveTypeDrops',
  async (params: any) => {
    const res = await getTargetsDropValues(params);
    return res.data;
  }
);

const initialState: AttendanceInitialStateType = {
  attendanceData: {
    pagination: BlankPagination,
    attendanceList: [],
  },
  leaveHistoryData: {
    pagination: BlankPagination,
    leaveList: [],
  },
  leaveTypes: [],
  statusDropOptions: [],
  LeaveTypeDropOptions: [],
  attendanceDetails: null,
  leaveDetails: leaveDetailsBlank,
  apiProgressStatus: '',
  approvedRejectedStatus: null,
};
export const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearAttendanceDetails: (state) => {
      state.attendanceDetails = null;
    },
    clearLeaveDetails: (state) => {
      state.leaveDetails = null;
    },
    clearApprovedRejectedStatus: (state) => {
      state.approvedRejectedStatus = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(approveOrRejectAttendance.fulfilled, (state, action) => {
        state.approvedRejectedStatus = action.payload.responseInformation;
      })
      .addCase(approveOrRejectAttendance.rejected, (state) => {
        state.approvedRejectedStatus = {
          responseCode: '1',
          responseMessage: 'Fail',
        };
      })
      .addCase(fetchAttendanceDetails.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          state.attendanceDetails = action.payload.result;
        } else {
          state.attendanceDetails = null;
        }
      })
      .addCase(fetchAttendanceDetails.rejected, (state) => {
        state.attendanceDetails = null;
      })
      .addCase(fetchAttendanceList.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.attendanceData.attendanceList = action.payload.result
            ? action.payload.result
            : [];
          state.attendanceData.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchAttendanceList.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchAttendanceList.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
        state.attendanceData.attendanceList = [];
        state.attendanceData.pagination = BlankPagination;
      })
      .addCase(fetchLeavesHistoryList.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.leaveHistoryData.leaveList = action.payload.result;
          state.leaveHistoryData.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchLeavesHistoryList.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchLeavesHistoryList.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
      })
      .addCase(fetchLeavesPendingList.fulfilled, (state, action) => {
        state.apiProgressStatus = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.leaveHistoryData.leaveList = action.payload.result;
          state.leaveHistoryData.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchLeavesPendingList.pending, (state) => {
        state.apiProgressStatus = API_STATUS.LOADING;
      })
      .addCase(fetchLeavesPendingList.rejected, (state) => {
        state.apiProgressStatus = API_STATUS.FAILED;
      })
      .addCase(fetchLeaveDetails.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          state.leaveDetails = action.payload.result;
        } else {
          state.leaveDetails = leaveDetailsBlank;
        }
      })
      .addCase(fetchStatusDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.statusId,
            label: item.statusName.replace('_', ' '),
          }));
          state.statusDropOptions = dropdowns;
        }
      })
      .addCase(fetchLeaveTypeDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.picklistId,
            label: item.picklistName,
          }));
          state.LeaveTypeDropOptions = dropdowns;
        }
      });
  },
});
export const attendanceSelector = (state: {
  attendance: AttendanceInitialStateType;
}) => state.attendance;

export const {
  clearAttendanceDetails,
  clearLeaveDetails,
  clearApprovedRejectedStatus,
} = attendanceSlice.actions;
export default attendanceSlice.reducer;
